import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import displayError from '../../utils/ErrorHandler';
import UserContext from '../../contexts/UserContext';
import { useNavigate, Link } from 'react-router-dom';
import authenticate from '../../utils/Authenticate';

const ListMenus = () => {
  const [menus, setMenus] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const [loading, setLoading] = useState(false);

  const goToCreateMenu = () => {
    navigate("/central/create-menu");
  };

  useEffect(() => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URI}/api/menu/getAll`, {
      withCredentials: true
    })
      .then(response => {
        setLoading(false);
        setMenus(response.data.data || []);
      })
      .catch((err) => {
        setLoading(false);
        const errorMessage = displayError(err, navigate, setUser);
        if (errorMessage) setMessage(errorMessage);
      });
  }, []);

  useEffect(() => {
    async function startup() {
      if (!user) await authenticate(setUser, navigate);
    }
    startup();
  }, [user, navigate, setUser]);

  const handleDelete = async (menu) => {
    if (confirmDelete !== menu._id) {
      setConfirmDelete(menu._id);
      return;
    }
    setDeletingId(menu._id);
    setConfirmDelete(null);
    try {
      const res = await axios.delete(`${process.env.REACT_APP_API_URI}/api/menu/delete`, {
        data: { menu: { _id: menu._id } },
        withCredentials: true,
      });
      if (res.data.success) {
        setMenus((prev) => prev.filter((m) => m._id !== menu._id));
      }
    } catch (err) {
      const errorMessage = displayError(err, navigate, setUser);
      if (errorMessage) setMessage(errorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredMenus = menus.filter(menu =>
    menu?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    menu?.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {loading ? (
        <div className='flex justify-center items-center h-[70vh] w-full'>
          <p>Loading Please Wait...</p>
        </div>
      ) : (
        <>
          <div className="p-4">

            {message && (
              <div className="bg-red-100 border border-red-300 text-red-500 px-4 py-3 mx-0 rounded relative" role="alert">
                <span className="block sm:inline">{message}</span>
              </div>
            )}

            <div className="mb-4">
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb"
                placeholder="Search menus..."
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredMenus.map(menu => (
                <div key={menu._id} className="bg-white p-4 rounded-lg shadow-md border border-fmb-light flex flex-col gap-3">
                  <Link to={`/central/menu/${menu._id}`} className="flex justify-between items-center hover:opacity-80 transition-opacity flex-1">
                    <div>
                      <h2 className="text-lg font-semibold text-fmb">{menu.name}</h2>
                      <div className="text-sm text-gray-700">
                        <span>{Array.isArray(menu.dishes) ? menu.dishes.length : 0} dishes</span>
                      </div>
                    </div>
                  </Link>

                  {/* Delete actions */}
                  <div className="flex gap-2 justify-end">
                    {confirmDelete === menu._id ? (
                      <>
                        <button
                          onClick={() => handleDelete(menu)}
                          disabled={deletingId === menu._id}
                          className="text-xs font-semibold px-3 py-1 rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 transition-colors"
                        >
                          {deletingId === menu._id ? '...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setConfirmDelete(null)}
                          className="text-xs text-gray-400 hover:text-gray-600"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleDelete(menu)}
                        className="text-xs font-semibold px-3 py-1 rounded-full border border-red-400 text-red-500 hover:bg-red-50 transition-colors"
                      >
                        Archive
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Floating Action Button */}
            <div className="fab-container" onClick={goToCreateMenu}>
              <button className="fab-button bg-fmb">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <span className="fab-text text-fmb">Add New Menu</span>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ListMenus;
